import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Form, Badge, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import CustomModal from '../componentes/Modal';
import FormularioUsuario from '../componentes/FormularioUsuario';
import Alerta from '../componentes/Alerta';
import { suscribirTotalUsuarios, obtenerUsuariosConPaginacion } from '../servicios/consultas';
import { crearUsuario, editarUsuario, eliminarUsuario, verificarUsuario } from '../servicios/crud';
import { db, auth } from '../servicios/firebase';
import '../componentes/UserManagement.css';

const UserManagement = ({ currentUser }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [ultimoDoc, setUltimoDoc] = useState(null);
  const [hayMas, setHayMas] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(10);
  const [editando, setEditando] = useState(null);
  const [alerta, setAlerta] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [cargandoTabla, setCargandoTabla] = useState(false);
  const [certificadosDetalle, setCertificadosDetalle] = useState({});

  // Filtrar usuarios basado en filterText
  const usuariosFiltrados = useMemo(() => {
    if (!filterText) return usuarios;
    
    const texto = filterText.toLowerCase();
    return usuarios.filter(usuario => {
      return (
        (usuario.nombre?.toLowerCase() || '').includes(texto) ||
        (usuario.apellido?.toLowerCase() || '').includes(texto) ||
        (usuario.cédula?.toString() || '').includes(texto) ||
        (usuario.email?.toLowerCase() || '').includes(texto) ||
        (usuario.estado?.toLowerCase() || '').includes(texto)
  )});
  }, [usuarios, filterText]);

  // ========== MANEJO DE ALERTAS ==========
  const mostrarAlerta = useCallback((tipo, titulo, contenido, onConfirm = null) => {
    const tiposValidos = ['success', 'error', 'warning', 'info', 'question'];
    setAlerta({
      tipo: tiposValidos.includes(tipo) ? tipo : 'info',
      titulo,
      contenido,
      onConfirm
    });
  }, []);

  // ========== EFECTO DE RECONEXIÓN ==========
  useEffect(() => {
    const reconectar = async () => {
      if (!currentUser) {
        await auth.signInWithCustomToken(sessionStorage.getItem('adminToken'));
      }
    };
    const timer = setTimeout(reconectar, 2000);
    return () => clearTimeout(timer);
  }, [currentUser]);

  // ========== CARGA PAGINADA DE USUARIOS ==========
  useEffect(() => {
    let isMounted = true;
    setCargandoTabla(true);

    const cargarUsuarios = async () => {
      try {
        const { usuarios: nuevosUsuarios, ultimoDoc: nuevoUltimoDoc, hayMas: nuevoHayMas } = 
          await obtenerUsuariosConPaginacion(registrosPorPagina, ultimoDoc);
        
        if (!isMounted) return;

        // Si es la primera página, reemplazamos, si no, concatenamos
        if (paginaActual === 1) {
          setUsuarios(nuevosUsuarios);
        } else {
          setUsuarios(prev => [...prev, ...nuevosUsuarios]);
        }
        
        setUltimoDoc(nuevoUltimoDoc);
        setHayMas(nuevoHayMas);
      } catch (error) {
        mostrarAlerta('error', 'Error', error.message);
      } finally {
        if (isMounted) {
          setCargandoTabla(false);
        }
      }
    };

    cargarUsuarios();

    return () => {
      isMounted = false;
    };
  }, [paginaActual, registrosPorPagina, mostrarAlerta]);

  // ========== MANEJADORES DE PAGINACIÓN ==========
  const cargarPaginaAnterior = () => {
    if (paginaActual > 1) {
      setPaginaActual(paginaActual - 1);
    }
  };

  const cargarPaginaSiguiente = () => {
    if (hayMas) {
      setPaginaActual(paginaActual + 1);
    }
  };

  const cambiarRegistrosPorPagina = (nuevoLimite) => {
    setRegistrosPorPagina(nuevoLimite);
    setPaginaActual(1); // Volver a la primera página
    setUltimoDoc(null); // Reiniciar el cursor
  };

  // ========== CERTIFICADOS POR USUARIO ==========
  useEffect(() => {
    const unsubscribes = [];
    
    if (usuarios.length > 0) {
      usuarios.forEach(usuario => {
        const q = query(collection(db, 'usuarios', usuario.id, 'certificados'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const certificados = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            estado: doc.data().estado || 'inactivo'
          }));
          
          const cursosActivos = [...new Set(certificados
            .filter(c => c.estado === 'activo')
            .map(c => c.curso))];
          
          const cursosInactivos = [...new Set(certificados
            .filter(c => c.estado !== 'activo')
            .map(c => c.curso))];

          setCertificadosDetalle(prev => ({
            ...prev,
            [usuario.id]: {
              activos: cursosActivos.length,
              inactivos: cursosInactivos.length,
              cursosActivos,
              cursosInactivos
            }
          }));
        });
        
        unsubscribes.push(unsubscribe);
      });
    }

    return () => unsubscribes.forEach(unsub => unsub());
  }, [usuarios]);

  // ========== TOTAL DE USUARIOS ==========
  useEffect(() => {
    let unsubscribeTotal;
    if (currentUser?.rol === 'superadmin') {
      unsubscribeTotal = suscribirTotalUsuarios(setTotal);
    }
    return () => unsubscribeTotal?.();
  }, [currentUser]);

  // ========== OPERACIONES CRUD ==========
  const manejarCreacion = useCallback(async (datos) => {
    try {
      setCargando(true);
      mostrarAlerta('info', 'Creando usuario', 'Por favor espere...');

      const uid = await crearUsuario({
        ...datos,
        password: datos.cédula.replace(/\D/g, '')
      });

      // Agregar el nuevo usuario al inicio y recargar la primera página
      setPaginaActual(1);
      setUltimoDoc(null);
      
      mostrarAlerta('success', 'Éxito', 'Usuario creado correctamente');
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    } finally {
      setModalAbierto(null);
      setCargando(false);
    }
  }, [mostrarAlerta]);

  const manejarEliminacion = useCallback(async (uid) => {
    try {
      setCargando(true);
      await eliminarUsuario(uid);
      // Recargar la página actual
      setPaginaActual(1);
      setUltimoDoc(null);
      mostrarAlerta('success', 'Éxito', 'Usuario eliminado correctamente');
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    } finally {
      setCargando(false);
    }
  }, [mostrarAlerta]);

  const manejarEdicion = useCallback(async (datos) => {
    try {
      setCargando(true);
      await editarUsuario(editando.id, datos);
      
      // Actualización optimista del estado
      setUsuarios(prev => prev.map(u => 
        u.id === editando.id ? { 
          ...u, 
          ...datos,
          contenidos: datos.contenidos.map(c => c.contenidoId)
        } : u
      ));
      
      mostrarAlerta('success', 'Éxito', 'Usuario actualizado correctamente');
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    } finally {
      setEditando(null);
      setCargando(false);
    }
  }, [editando, mostrarAlerta]);

  const manejarVerificacion = useCallback(async (usuario) => {
    try {
      setCargando(true);
      
      const cursosActivos = certificadosDetalle[usuario.id]?.cursosActivos || [];
      
      // Validación de cursos activos
      if (cursosActivos.length === 0) {
        mostrarAlerta('warning', 'Verificación no realizada', 'El usuario no tiene certificados activos');
        return;
      }

      const datosVerificacion = {
        nombreCompleto: `${usuario.nombre} ${usuario.apellido}`,
        cedula: usuario.cédula,
        cursosActivos,
        fechaVerificacion: new Date().toISOString(),
        verificador: currentUser?.email || 'Sistema'
      };

      await verificarUsuario(datosVerificacion);
      mostrarAlerta('success', 'Verificación exitosa', 'Registro guardado con cursos activos');
      
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    } finally {
      setCargando(false);
    }
  }, [certificadosDetalle, currentUser, mostrarAlerta]);

  // ========== COLUMNAS ACTUALIZADAS CON SVG ==========
  const columnas = useMemo(() => [
    {
      id: 'nombre', // Identificador único
      name: 'Nombre Completo',
      selector: row => `${row.nombre} ${row.apellido}`,
      sortable: true,
      wrap: true,
      minWidth: '180px',
      style: {
        fontWeight: '600',
        color: '#273b47'
      },
      omit: false
    },
    {
      id: 'estado', // Identificador único
      name: 'Cédula',
      selector: row => row.cédula,
      sortable: true,
      hide: 'sm',
      maxWidth: '120px'
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      wrap: true,
      minWidth: '200px',
      hide: 'md'
    },
    {
      name: 'Estado',
      selector: row => row.estado,
      sortable: true,
      cell: row => (
        <Badge pill bg={row.estado === 'activo' ? 'success' : 'danger'}>
          {row.estado}
        </Badge>
      ),
      maxWidth: '100px',
      // Forzar visibilidad permanente
      omit: false 
    },
    // ========== ACTUALIZAR COLUMNA DE CERTIFICADOS ==========
    {
      name: 'Certificados',
      selector: row => certificadosDetalle[row.id]?.activos || 0,
      cell: row => (
        <div className="d-flex flex-wrap gap-1">
          {/* Cursos Activos */}
          {certificadosDetalle[row.id]?.cursosActivos?.map((curso, index) => (
            <Badge 
              key={`activo-${index}`} 
              pill 
              bg="success" 
              className="opacity-90"
            >
              {curso}
            </Badge>
          ))}
          
          {/* Cursos Inactivos */}
          {certificadosDetalle[row.id]?.cursosInactivos?.map((curso, index) => (
            <Badge 
              key={`inactivo-${index}`}
              pill 
              bg="secondary" 
              className="opacity-50 text-muted"
              style={{ backgroundColor: '#6c757d33' }}
            >
              {curso}
            </Badge>
          ))}
        </div>
      ),
      minWidth: '220px',
      wrap: true
    },
    {
      name: 'Acciones',
      button: true,
      cell: row => (
        <div className="d-none d-lg-flex gap-2">
          {/* Botón Editar */}
          <Button
            variant="outline-primary"
            onClick={() => setEditando(row)}
            className="table-btn table-edit-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
              <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
              <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
            </svg>
          </Button>
    
          {/* Nuevo Botón Verificar */}
          <Button
            variant="outline-success"
            onClick={() => manejarVerificacion(row)}
            className="table-btn table-verify-btn"
            title="Verificar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-clipboard-check" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0"/>
              <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1z"/>
              <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0z"/>
            </svg>
          </Button>
    
          {/* Botón Eliminar */}
          <Button
            variant="outline-danger"
            onClick={() => mostrarAlerta(
              'warning',
              'Confirmar',
              '¿Eliminar usuario permanentemente?',
              () => manejarEliminacion(row.id)
            )}
            className="table-btn table-delete-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash3-fill" viewBox="0 0 16 16">
              <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
            </svg>
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      minWidth: '160px', // Aumentado para 3 botones
      hide: 'md'
    },
  ], [certificadosDetalle, mostrarAlerta, manejarEliminacion, manejarVerificacion]);

  // ========== COMPONENTE EXPANDIBLE MEJORADO ==========
  const ExpandableComponent = ({ data }) => (
    <div className="p-3 bg-light" style={{ marginTop: '-10px' }}>
      <div className="mb-2">
        <strong>Cédula:</strong> {data.cédula}
      </div>
      <div className="mb-2">
        <strong>Email:</strong> {data.email}
      </div>
      
      <div className="mb-2">
        <strong>Certificados:</strong>
        <div className="mt-2">
          
          {/* Sección Activos */}
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Badge pill bg="success" className="opacity-90">Activos</Badge>
              <small className="text-muted">({certificadosDetalle[data.id]?.activos})</small>
            </div>
            <div className="d-flex flex-wrap gap-1">
              {certificadosDetalle[data.id]?.cursosActivos?.map((curso, index) => (
                <Badge 
                  key={`activo-${index}`}
                  bg="success" 
                  className="opacity-90 text-white"
                >
                  {curso}
                </Badge>
              ))}
            </div>
          </div>

          {/* Sección Inactivos */}
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <Badge pill bg="secondary" className="opacity-50">Inactivos</Badge>
              <small className="text-muted">({certificadosDetalle[data.id]?.inactivos})</small>
            </div>
            <div className="d-flex flex-wrap gap-1">
              {certificadosDetalle[data.id]?.cursosInactivos?.map((curso, index) => (
                <Badge 
                  key={`inactivo-${index}`}
                  bg="secondary" 
                  className="opacity-50 text-muted"
                  style={{ backgroundColor: 'rgba(108, 117, 125, 0.2)' }}
                >
                  {curso}
                </Badge>
              ))}
            </div>
          </div>

        </div>
      </div>
      <div className="d-lg-none mt-2">
        <Button
          variant="outline-primary"
          onClick={() => setEditando(data)}
          className="cm-table-btn cm-table-edit-btn w-100 mb-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-pencil-square me-1" viewBox="0 0 16 16">
            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
            <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
          </svg>
        </Button>

        <Button
          variant="outline-success"
          onClick={() => manejarVerificacion(data)}
          className="cm-table-btn cm-table-verify-btn w-100 mb-2"
          title="Verificar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-clipboard-check me-1" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0"/>
            <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1z"/>
            <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0z"/>
          </svg>
        </Button>

        <Button
          variant="outline-danger"
          onClick={() => mostrarAlerta(
            'warning',
            'Confirmar',
            '¿Eliminar usuario permanentemente?',
            () => manejarEliminacion(data.id)
          )}
          className="cm-table-btn cm-table-delete-btn w-100"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-trash3-fill me-1" viewBox="0 0 16 16">
            <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
          </svg>
        </Button>
      </div>
    </div>
  );

  // ========== TABLA ESTILIZADA MEJORADA ==========
  const TablaEstilizada = styled(DataTable)`
    border-radius: 15px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

    .rdt_TableHead {
      background-color: #273b47 !important;
      > div {
        background-color: transparent !important;
        padding: 0 !important;
      }

      @media (max-width: 768px) {
        .rdt_TableCol:first-child {
          border-radius: 15px 0 0 0 !important;
          padding-left: 15px !important;
        }
        
        .rdt_TableCol:nth-child(4) {
          border-radius: 0 15px 0 0 !important;
          padding-right: 15px !important;
        }
      }
    }

    @media (max-width: 992px) {
      .rdt_Table {
        display: block;
        width: 100%;
      }
      
      .rdt_TableBody {
        display: block;
        overflow: hidden !important;
      }

      .rdt_TableCell {
        padding: 12px 8px !important;
      }
    }

    @media (max-width: 768px) {
      .rdt_TableHeadRow {
        border-top-left-radius: 15px !important;
        border-top-right-radius: 15px !important;
      }
    }
  `;

  // ========== COMPONENTE DE PAGINACIÓN PERSONALIZADO ==========
  const PaginacionPersonalizada = () => (
    <div className="d-flex justify-content-between align-items-center mt-3 cm-pagination">
      <Button 
        variant="outline-primary"
        disabled={paginaActual === 1 || cargandoTabla}
        onClick={cargarPaginaAnterior}
        className="cm-pagination-btn"
      >
        Anterior
      </Button>
      
      <span className="mx-2">Página {paginaActual}</span>
      
      <Button 
        variant="outline-primary"
        disabled={!hayMas || cargandoTabla}
        onClick={cargarPaginaSiguiente}
        className="cm-pagination-btn"
      >
        Siguiente
      </Button>
      
      <Form.Select 
        value={registrosPorPagina}
        onChange={(e) => cambiarRegistrosPorPagina(Number(e.target.value))}
        className="ms-3"
        style={{ width: '100px' }}
        disabled={cargandoTabla}
      >
        <option value={10}>10</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
      </Form.Select>
    </div>
  );

  return (
    <>
      {alerta && (
        <Alerta
          type={alerta.tipo}
          title={alerta.titulo}
          content={alerta.contenido}
          onConfirm={alerta.onConfirm || (() => setAlerta(null))}
          onCancel={() => setAlerta(null)}
        />
      )}

      <div className="tab-content">
        <div className="header-section d-flex justify-content-between align-items-center mb-4">
          <h2 className="m-0" style={{ color: '#273b47' }}>Administración de Usuarios ({total})</h2>
          <Button
            onClick={() => setModalAbierto('nuevo')}
            className="custom-btn primary"
            disabled={cargando}
            style={{ backgroundColor: '#98ca3f', border: 'none' }}
          >
            {cargando ? <Spinner size="sm" /> : '+ Nuevo Usuario'}
          </Button>
        </div>

        <Form.Control
          type="text"
          placeholder="🔍 Buscar usuarios..."
          onChange={e => setFilterText(e.target.value)}
          value={filterText}
          className="custom-search-input mb-3"
        />

        <TablaEstilizada
          columns={columnas}
          data={usuariosFiltrados}
          pagination={false} // Deshabilitamos paginación nativa
          responsive
          highlightOnHover
          expandableRows
          expandableRowsComponent={ExpandableComponent}
          progressPending={cargandoTabla}
          progressComponent={<Spinner animation="border" variant="primary" className="my-4" />}
          noDataComponent={<div className="py-4 text-center">No se encontraron usuarios</div>}
          customStyles={{
            headCells: {
              style: {
                backgroundColor: '#273b47',
                color: '#f3f8fb',
                fontSize: '0.95rem',
              },
            },
            cells: {
              style: {
                color: '#7e96a5',
                fontSize: '0.9rem',
              },
            },
          }}
        />

        <PaginacionPersonalizada />

        <CustomModal 
          show={modalAbierto === 'nuevo'} 
          handleClose={() => setModalAbierto(null)} 
          title="Nuevo Usuario"
        >
          <FormularioUsuario 
            onSubmit={manejarCreacion} 
            onCancel={() => setModalAbierto(null)} 
            disabled={cargando} 
          />
        </CustomModal>

        <CustomModal 
          show={!!editando} 
          handleClose={() => setEditando(null)} 
          title="Editar Usuario"
        >
          <FormularioUsuario 
            onSubmit={manejarEdicion} 
            onCancel={() => setEditando(null)} 
            initialData={editando} 
            disabled={cargando} 
          />
        </CustomModal>
      </div>
    </>
  );
};

export default UserManagement;